/**
 * Buy Sheet view — pricing-target watchlist (read-only display).
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { createRowSelection } from "../components/rowSelection";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$ } from "../utils/format";
import { openModal, closeModal } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { render } from "../state";
import type { Row } from "../lib/types";

const sel = createRowSelection("buys");
function buysEdit(uuid: string): void { (window as any).editBuy?.(uuid); }
function buysDelete(uuid: string): void { (window as any).deleteBuy?.(uuid); }

function findBuyByUuid(uuid: string): Row | null {
  if (!uuid) return null;
  return DB.table("buys").find(r => r && r._uuid === uuid) || null;
}

export function viewBuys(root: HTMLElement): void {
  const buys = DB.table("buys").slice();

  const buys_total = buys.length;
  const buys_page = slicePage("buys", buys, 50);
  root.innerHTML = `
    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: buysEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: buysDelete },
      ])}
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Target</th><th>Category</th>
          <th class="text-right">Market</th><th class="text-right">Buy %</th>
          <th class="text-right">Max Price</th><th class="text-right">Margin</th>
          <th>Priority</th><th>Notes</th>
        </tr></thead>
        <tbody>
          ${buys_page.map(r => {
            const u = escapeHtml(r._uuid || "");
            return `<tr ${sel.rowAttrs(u, `editBuy('${u}')`)}>
              <td>${escapeHtml(r["Target"] || "")}</td>
              <td>${escapeHtml(r["Category"] || "")}</td>
              <td class="text-right stat-num">${fmt$(r["Market"])}</td>
              <td class="text-right stat-num">${r["Target Buy %"] ? Number(r["Target Buy %"]).toFixed(0) + "%" : ""}</td>
              <td class="text-right stat-num text-amber-300">${fmt$(r["Max Buy Price"])}</td>
              <td class="text-right stat-num">${fmt$(r["Expected Margin"])}</td>
              <td>${escapeHtml(r["Priority"] || "")}</td>
              <td class="text-xs text-[var(--muted)]" title="${escapeHtml(r["Notes"] || "")}">${escapeHtml(r["Notes"] || "")}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="8" class="text-center text-[var(--muted)] py-8">No targets yet.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("buys", buys_total, 50)}
  `;
  sel.applyUI();
}

/* ============================================================
   Buy target form (editBuy / saveBuy / deleteBuy)
   ============================================================ */
export function editBuy(uuid?: string): void {
  const isNew = !uuid;
  const row = isNew ? {} : findBuyByUuid(uuid!);
  if (!row) { toast("Target not found", "err"); return; }

  const body = `
    <form id="buyForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      <div class="sm:col-span-2">${formField({ label: "Target", name: "Target", value: row["Target"], required: true })}</div>
      ${formField({ label: "Category", name: "Category", value: row["Category"], options: ["Sealed", "Singles", "Slabs", "Other"] })}
      ${formField({ label: "Market ($)", name: "Market", type: "number", value: row["Market"], hint: "Current market price" })}
      ${formField({ label: "Target Buy %", name: "Target Buy %", type: "number", value: row["Target Buy %"], hint: "E.g. 70 = pay 70% of market" })}
      ${formField({ label: "Max Buy Price ($)", name: "Max Buy Price", type: "number", value: row["Max Buy Price"], hint: "Auto = Market × Buy %" })}
      ${formField({ label: "Expected Margin ($)", name: "Expected Margin", type: "number", value: row["Expected Margin"] })}
      ${formField({ label: "Priority", name: "Priority", value: row["Priority"], options: ["High", "Medium", "Low"] })}
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
    </form>`;
  const u = isNew ? "" : escapeHtml(uuid!);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteBuy('${u}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveBuy('${u}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Target" : "Edit Target", body, footer });
}

export async function saveBuy(uuid: string): Promise<void> {
  const d = readForm($("#buyForm"));
  // Auto-fill Max Buy Price when both Market and Buy % are supplied.
  if (!d["Max Buy Price"] && d["Market"] && d["Target Buy %"]) {
    d["Max Buy Price"] = +(Number(d["Market"]) * Number(d["Target Buy %"]) / 100).toFixed(2);
  }
  try {
    if (!uuid) await DB.add("buys", d);
    else       await DB.updateByUuid("buys", uuid, d);
  } catch (e: any) {
    console.error("[saveBuy]", e);
    toast("Cloud sync failed: " + (e?.message || e), "err");
    return;
  }
  toast("Target saved");
  closeModal();
  render();
}

export async function deleteBuy(uuid: string): Promise<void> {
  if (!uuid) return;
  if (!confirm("Delete this target?")) return;
  try { await DB.removeByUuid("buys", uuid); }
  catch (e: any) {
    console.error("[deleteBuy]", e);
    toast("Cloud delete failed: " + (e?.message || e), "err");
    return;
  }
  toast("Deleted");
  closeModal();
  render();
}

(window as any).editBuy = editBuy;
(window as any).saveBuy = saveBuy;
(window as any).deleteBuy = deleteBuy;
